import React, { useEffect, useRef } from "react";

const focusableSelectors = [
  "a[href]",
  "area[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "button:not([disabled])",
  "iframe",
  "object",
  "embed",
  '[tabindex="0"]',
  "[contenteditable]",
  "div[role='menuitem']"
] as const;

const focusableElementsAsString = focusableSelectors.join(",");

const handleTabNavigation = (
  event: KeyboardEvent,
  activeElement: Element | null,
  firstTabStop: HTMLElement,
  lastTabStop: HTMLElement,
  isInsideContainer: boolean,
  moveFocus: (target: HTMLElement) => void
) => {
  if (!isInsideContainer) {
    moveFocus(event.shiftKey ? lastTabStop : firstTabStop);
  } else if (event.shiftKey && activeElement === firstTabStop) {
    moveFocus(lastTabStop);
  } else if (!event.shiftKey && activeElement === lastTabStop) {
    moveFocus(firstTabStop);
  } else {
    return;
  }
};

const handleEscapeKey = (close: () => void) => {
  close();
  const activeElement = document.activeElement as HTMLElement | null;
  activeElement?.blur();
};

const trapTabKey = <T extends HTMLElement>(
  event: KeyboardEvent,
  close: () => void,
  containerRef: React.RefObject<T>
): void => {
  const containerElement = containerRef.current;

  if (!containerElement) {
    return;
  }

  const focusableElements = Array.from(
    containerElement.querySelectorAll<HTMLElement>(focusableElementsAsString)
  );

  if (focusableElements.length === 0) {
    return;
  }

  const firstTabStop = focusableElements[0];
  const lastTabStop = focusableElements[focusableElements.length - 1];
  const { activeElement } = document;

  const moveFocus = (target: HTMLElement) => {
    if (document.contains(target)) {
      event.preventDefault();
      target.focus();
    }
  };

  const isInsideContainer = containerElement.contains(activeElement);
  const eventKey = event.key;
  const isTabKey = eventKey === 'Tab';
  const isEscKey = eventKey === 'Escape' || eventKey === 'Esc';
  if (isTabKey) {
    return handleTabNavigation(
      event,
      activeElement,
      firstTabStop,
      lastTabStop,
      isInsideContainer,
      moveFocus
    );
  } else if (isEscKey) {
    return handleEscapeKey(close);
  } else {
    return;
  }
};

export const useFocusTrap = <T extends HTMLElement = HTMLDivElement>(closeHandler = () => {}, isActive = true) => {
  const focusTrapContainerRef = useRef<T>(null);

  useEffect(() => {
    if (!isActive) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) =>
      trapTabKey(event, closeHandler, focusTrapContainerRef);

    document.addEventListener("keydown", handleKeyDown);

    const firstFocusableElement = focusTrapContainerRef.current?.querySelector<T>(focusableElementsAsString);
    if (firstFocusableElement) {
      firstFocusableElement.focus();
    }

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [closeHandler, isActive]);

  return focusTrapContainerRef;
};
